import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { X, Play, ArrowRight, Award, CalendarDays, Sparkles, Lock } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import ContentTile from "../components/ContentTile";
import VideoPlayer from "../components/VideoPlayer";
import { useAuth } from "../context/AuthContext";
import { TIER_NAME, TIER_LABEL, nextTier, TIER_RANK } from "../lib/tiers";
import { QUIZ_KEY_BY_CONTENT } from "../lib/quizzes";
import api, { formatApiError, mediaUrl } from "../lib/api";

const CATEGORY_ORDER = ["Tai Chi Kung", "Breathwork", "Yoga"];
const RESOURCE_ORDER = ["Assessment Tool", "Recipe/Remedy", "Reflection", "Recommendation Hub", "Seasonal Guidance", "Free Guide", "Free Ebook"];

function Section({ title, children }) {
  return (
    <div className="mb-10">
      <h3 className="font-display text-2xl font-semibold text-brand-ink mb-4">{title}</h3>
      {children}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [content, setContent] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [progress, setProgress] = useState({ items: [], videos_watched: 0, videos_total: 23 });
  const [consult, setConsult] = useState({ bookings: [], allotment: 1, used: 0, remaining: 1 });
  const [assessments, setAssessments] = useState([]);
  const [modal, setModal] = useState(null);
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "videos");

  const load = async () => {
    const [c, t, p, cs, a] = await Promise.all([
      api.get("/content"), api.get("/trainings"), api.get("/progress"), api.get("/consultations"), api.get("/assessments"),
    ]);
    setContent(c.data); setTrainings(t.data); setProgress(p.data); setConsult(cs.data); setAssessments(a.data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setModal(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const assessmentMap = useMemo(() => {
    const m = {};
    assessments.forEach((a) => { m[a.quiz_key] = a.result; });
    return m;
  }, [assessments]);

  const completedMap = useMemo(() => {
    const m = {};
    progress.items.forEach((i) => { if (i.completed) m[i.item_id] = true; });
    return m;
  }, [progress]);

  const toggleComplete = async (id, val) => {
    try {
      await api.post("/progress", { item_id: id, completed: val });
      const p = await api.get("/progress"); setProgress(p.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openItem = (item) => {
    // Interactive quizzes for the Silver assessment tools
    const quizKey = QUIZ_KEY_BY_CONTENT[item.id];
    if (quizKey && item.unlocked) { navigate(`/quiz/${quizKey}`); return; }
    if (item.type === "video") setModal(item);
    else window.open(item.source_url || "https://isoharts.com", "_blank");
    if (item.unlocked && !completedMap[item.id]) toggleComplete(item.id, true);
  };

  const videos = content.filter((c) => c.type === "video");
  const resources = content.filter((c) => c.type === "resource");
  const nt = nextTier(user.tier);

  const grouped = (list, order) => order
    .map((cat) => ({ cat, items: list.filter((i) => i.category === cat) }))
    .filter((g) => g.items.length);

  return (
    <Layout>
      <div className="section-pad !py-10 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-brand-muted text-sm">Welcome back,</p>
            <h1 className="font-display text-4xl font-semibold text-brand-ink">{user.name}</h1>
          </div>
          <div className="flex items-center gap-3" data-testid="tier-badge">
            <span className="pill text-white" style={{ background: user.tier === "diamond" ? "#4F8176" : user.tier === "gold" ? "#C59B27" : "#718078" }}>
              <Award size={12} className="inline mr-1" /> {TIER_NAME[user.tier]}
            </span>
            {nt && <Link to={`/packages?tier=${nt}&checkout=true`} className="btn-primary !py-2 !px-4 text-sm" data-testid="upgrade-button">Upgrade</Link>}
          </div>
        </div>

        {/* Progress + Astrology + Consult summary */}
        <div className="grid md:grid-cols-3 gap-5 mt-8">
          <div className="card-surface p-6">
            <p className="text-sm text-brand-muted">Video Progress</p>
            <p className="font-display text-3xl font-semibold text-brand-ink mt-1" data-testid="progress-count">{progress.videos_watched} of {progress.videos_total} watched</p>
            <div className="mt-3 h-2 rounded-full bg-brand-cream2 overflow-hidden">
              <div className="h-full bg-brand-sage transition-all" style={{ width: `${(progress.videos_watched / progress.videos_total) * 100}%` }} />
            </div>
          </div>
          <div className="card-surface p-6">
            <p className="text-sm text-brand-muted">Consultations</p>
            <p className="font-display text-3xl font-semibold text-brand-ink mt-1" data-testid="consult-remaining">{consult.remaining} remaining</p>
            <p className="text-xs text-brand-muted mt-1">{consult.used} used of {consult.allotment} this year</p>
            <button onClick={() => setTab("consult")} className="text-brand-sage text-sm font-semibold mt-2 hover:underline">Book a session →</button>
          </div>
          <Link to="/chinese-astrology" className="card-surface p-6 hover:shadow-md transition-shadow" data-testid="astrology-card">
            <Sparkles className="text-brand-gold" />
            <p className="font-display text-xl font-semibold text-brand-ink mt-2">Chinese Astrology</p>
            <p className="text-xs text-brand-muted mt-1">Instant Five Elements reading from your birth date →</p>
          </Link>
        </div>

        {/* Upgrade banner */}
        {nt && <UpgradeBanner nt={nt} />}

        {/* Tabs */}
        <div className="mt-10 flex gap-2 border-b border-brand-line overflow-x-auto no-scrollbar">
          {[["videos", "Video Library"], ["resources", "Resources"], ["trainings", "Trainings"], ["consult", "Consultations"]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === k ? "border-brand-sage text-brand-sage" : "border-transparent text-brand-muted hover:text-brand-ink"}`}>
              {label}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "videos" && grouped(videos, CATEGORY_ORDER).map((g) => (
            <Section key={g.cat} title={g.cat}>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                {g.items.map((it) => <ContentTile key={it.id} item={it} completed={completedMap[it.id]} onOpen={openItem} onToggle={toggleComplete} />)}
              </div>
            </Section>
          ))}

          {tab === "resources" && (
            <>
              <div className="card-surface p-6 mb-8" data-testid="quiz-results-panel">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <h3 className="font-display text-2xl font-semibold text-brand-ink">Your Interactive Assessments</h3>
                    <p className="text-sm text-brand-muted mt-1">Take a quiz — your results are saved here and persist across visits.</p>
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-4 mt-5">
                  {[["thinking-style", "Thinking Style"], ["5-seasons", "5 Seasons — Who Am I?"], ["7-levels", "7 Levels of Vital Health"]].map(([k, label]) => (
                    <Link to={`/quiz/${k}`} key={k} data-testid={`dash-quiz-${k}`}
                      className="rounded-xl border border-brand-line p-4 hover:border-brand-sage hover:bg-brand-cream transition-colors">
                      <div className="flex items-center gap-2 text-brand-gold"><Sparkles size={16} /><span className="text-xs uppercase tracking-wide text-brand-muted">Assessment</span></div>
                      <h4 className="font-medium text-brand-ink mt-2 text-sm">{label}</h4>
                      {assessmentMap[k] ? (
                        <p className="text-xs text-brand-sage mt-2 font-medium">Result: {assessmentMap[k]}</p>
                      ) : (
                        <p className="text-xs text-brand-muted mt-2">Not taken yet — tap to begin →</p>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
              {grouped(resources, RESOURCE_ORDER).map((g) => (
                <Section key={g.cat} title={g.cat}>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                    {g.items.map((it) => <ContentTile key={it.id} item={it} completed={completedMap[it.id]} onOpen={openItem} onToggle={toggleComplete} />)}
                  </div>
                </Section>
              ))}
            </>
          )}

          {tab === "trainings" && (
            <div className="grid md:grid-cols-3 gap-6">
              {trainings.map((tr) => (
                <div key={tr.id} className="card-surface overflow-hidden flex flex-col" data-testid={`training-${tr.id}`}>
                  <div className="relative aspect-video">
                    <img src={mediaUrl(tr.thumbnail)} alt={tr.title} className={`w-full h-full object-cover ${!tr.unlocked ? "blur-sm" : ""}`} />
                    {!tr.unlocked && <div className="absolute inset-0 bg-black/45 flex items-center justify-center"><Lock className="text-brand-gold" /></div>}
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <h4 className="font-display text-xl font-semibold text-brand-ink">{tr.title}</h4>
                    <p className="text-sm text-brand-inksoft mt-2 flex-1">{tr.teaser}</p>
                    {tr.unlocked ? (
                      <Link to={`/trainings/${tr.id}`} className="btn-primary mt-4 !py-2 text-sm">Open Training</Link>
                    ) : (
                      <Link to="/packages?tier=diamond&checkout=true" className="btn-gold mt-4 !py-2 text-sm">Unlock with Diamond — $50</Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "consult" && <ConsultScheduler consult={consult} reload={load} />}
        </div>
      </div>

      {/* Video modal */}
      {modal && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-5" onClick={() => setModal(null)}>
          <div className="bg-black rounded-2xl overflow-hidden max-w-3xl w-full" onClick={(e) => e.stopPropagation()} data-testid="video-modal">
            <div className="flex items-center justify-between px-4 py-3 bg-brand-ink text-white">
              <span className="text-sm font-medium truncate">{modal.title}</span>
              <button onClick={() => setModal(null)} data-testid="video-modal-close"><X size={18} /></button>
            </div>
            <VideoPlayer itemId={modal.id} embedUrl={modal.embed_url} sourceUrl={modal.source_url} title={modal.title} />
            <div className="px-4 py-3 bg-brand-ink text-white/60 text-xs">
              Original lesson on isoharts.com: <a href={modal.source_url} target="_blank" rel="noreferrer" className="text-brand-gold underline break-all">{modal.source_url}</a>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function UpgradeBanner({ nt }) {
  const perks = {
    gold: "Unlock 6 more videos, the full recipe & remedy library, seasonal guidance, 5 yearly consultations, and the Shaping Our Destiny ebook.",
    diamond: "Unlock all 23 videos, every reflection, all 3 signature trainings, emotional mastery teachings, and 10 yearly consultations.",
  };
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="upgrade-banner"
      className="mt-6 rounded-2xl p-6 flex flex-wrap items-center justify-between gap-4"
      style={{ background: "linear-gradient(100deg,#4E6E5D,#3D5849)" }}>
      <div className="text-white max-w-2xl">
        <p className="font-display text-2xl font-semibold">Go further with {TIER_NAME[nt]}</p>
        <p className="text-white/80 text-sm mt-1">{perks[nt]}</p>
      </div>
      <Link to={`/packages?tier=${nt}&checkout=true`} className="btn-gold whitespace-nowrap">Upgrade to {TIER_LABEL[nt]} <ArrowRight size={16} /></Link>
    </motion.div>
  );
}

function ConsultScheduler({ consult, reload }) {
  const SERVICES = ["Discovery Call (20 min)", "Zen-Touch Therapy", "Energy Assessment", "Chinese Astrology Reading"];
  const [form, setForm] = useState({ date: "", time: "09:00", service: SERVICES[0], notes: "" });
  const [busy, setBusy] = useState(false);

  const book = async (e) => {
    e.preventDefault();
    if (consult.remaining <= 0) { toast.error("No consultation credits remaining this period."); return; }
    setBusy(true);
    try {
      await api.post("/consultations", form);
      toast.success("Consultation booked with Seymour!");
      await reload();
      setForm({ ...form, date: "", notes: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const cancel = async (id) => { await api.delete(`/consultations/${id}`); toast.success("Booking cancelled."); reload(); };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <form onSubmit={book} className="card-surface p-6 space-y-4" data-testid="consultation-booking-calendar">
        <h4 className="font-display text-xl font-semibold text-brand-ink flex items-center gap-2"><CalendarDays size={18} className="text-brand-sage" /> Book a Consultation</h4>
        <p className="text-sm text-brand-muted">{consult.remaining} of {consult.allotment} credits remaining this year.</p>
        <div className="grid grid-cols-2 gap-3">
          <input type="date" required className="input-field" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="consult-date" />
          <input type="time" required className="input-field" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="consult-time" />
        </div>
        <select className="input-field" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} data-testid="consult-service">
          {SERVICES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <textarea placeholder="Anything you'd like Seymour to know? (optional)" className="input-field" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        <button disabled={busy || consult.remaining <= 0} className="btn-primary w-full" data-testid="consult-submit">{busy ? "Booking…" : "Confirm Booking"}</button>
      </form>
      <div>
        <h4 className="font-display text-xl font-semibold text-brand-ink mb-3">Your Bookings</h4>
        {consult.bookings.filter((b) => b.status !== "cancelled").length === 0 ? (
          <p className="text-brand-muted text-sm">No consultations booked yet.</p>
        ) : (
          <div className="space-y-3">
            {consult.bookings.filter((b) => b.status !== "cancelled").map((b) => (
              <div key={b.id} className="card-surface p-4 flex items-center justify-between" data-testid={`booking-${b.id}`}>
                <div>
                  <p className="font-medium text-brand-ink text-sm">{b.service}</p>
                  <p className="text-xs text-brand-muted">{b.date} at {b.time}</p>
                </div>
                <button onClick={() => cancel(b.id)} className="text-xs text-red-500 hover:underline">Cancel</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
